export const Manager_SOP = {
    managerOnboarding: {
        title: "Staff Onboarding & Orientation",
        body: `
          <section>
            <h3>👋 Welcome to the Pack: Onboarding With Purpose</h3>
            <p>
              A strong first impression shapes long-term success. At Pawsible, onboarding is more than paperwork — 
              it’s <span class="highlight">a guided journey into our culture, expectations, and commitment to exceptional care</span>.
            </p>
          </section>
      
          <section>
            <h4>📋 1. First-Day Essentials</h4>
            <ul>
              <li>👕 Issue uniform, name tag, and locker/cubby assignment</li>
              <li>📱 Help install and log into internal apps (task board, time clock, training portal)</li>
              <li>📚 Provide printed or digital staff handbook + highlight SOP categories relevant to their role</li>
              <li>🎓 Introduce Pawsible philosophy and our customer experience values</li>
              <li>👥 Pair with a designated mentor or department lead</li>
            </ul>
            <p><span class="highlight-blue">Confidence starts when expectations are clear — make every new hire feel informed and included.</span></p>
          </section>
      
          <section>
            <h4>🎓 2. Orientation Milestones</h4>
            <ul>
              <li>🧠 Week 1: Complete core safety modules, SOP tour, and basic facility walkthrough</li>
              <li>👀 Shadow at least 1 full day time to time in their department</li>
              <li>📝 Assign checklists and quizzes for each role-specific training</li>
              <li>🗣️ Hold a 15-min check-in conversation at the end of first week</li>
            </ul>
            <p><span class="highlight-green">Orientation is not a one-day task — it’s a phased experience that builds skill and belonging.</span></p>
          </section>
      
          <section>
            <h4>🛠️ 3. Manager Setup & Tracking</h4>
            <ul>
              <li>📁 Track onboarding record in Manager Dashboard</li>
              <li>📅 Assign training timeline and mark expected completion dates</li>
              <li>📲 Track SOP completion and quiz results in the training dashboard</li>
              <li>📩 If onboarding is delayed or issues arise, notify leadership for support</li>
            </ul>
            <p><span class="highlight-blue">Consistent records = smoother transitions, clearer evaluations, and fair treatment across all hires.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">People remember how they’re welcomed.</span> 
              A great onboarding experience creates loyalty, performance, and culture. 
              As a manager, <strong>you set the tone — make it strong, warm, and intentional</strong>.
            </p>
          </section>
        `
      },
      managerShadowingTraining: {
        title: "Shadowing & Training Pathways",
        body: `
          <section>
            <h3>👣 Learn by Doing, Grow by Observing</h3>
            <p>
              At Pawsible, we don’t just assign shifts — we build <span class="highlight-green">confident, competent team members through layered learning</span>. 
              Shadowing and training are designed to <span class="highlight">bridge knowledge with real-world execution</span>.
            </p>
          </section>
      
          <section>
            <h4>🔍 1. Shadowing Expectations by Role</h4>
            <ul>
              <li>🧸 <strong>Daycare:</strong> Observe group energy, redirection cues, and staff positioning</li>
              <li>🛁 <strong>Grooming:</strong> Watch pre-groom check-in, handling techniques, and sanitation</li>
              <li>🐕 <strong>Training:</strong> Follow lead trainer’s tone, timing, and communication with both dog and parent</li>
              <li>🛎️ <strong>Front Desk:</strong> Listen in on tours, booking flow, upsell conversations, and phone etiquette</li>
              <li>🚌 <strong>Shuttle:</strong> Ride-along for route safety, pickup timing, and crate handling</li>
            </ul>
            <p><span class="highlight-blue">Shadowing is active learning — watch body language, tools used, and the rhythm of a polished shift.</span></p>
          </section>
      
          <section>
            <h4>🧭 2. Structured Training Pathways</h4>
            <ul>
              <li>🗂️ Each department has its own learning blocks (e.g. Daycare Safety 101, Grooming Matting Policy)</li>
              <li>✅ Assign modules with matching SOPs + visual guides or training videos</li>
              <li>🧪 Include short check-ins or quizzes after each concept to reinforce learning</li>
              <li>📆 Use a clear weekly schedule: "Week 1: Observe", "Week 2: Assist", "Week 3: Lead under supervision"</li>
            </ul>
            <p><span class="highlight-green">Structure builds trust — both in the staff’s development and in your training system.</span></p>
          </section>
      
          <section>
            <h4>📋 3. Documentation & Feedback</h4>
            <ul>
              <li>📝 Use a shared tracking dashboard to log what are completed</li>
              <li>🗣️ Give feedback immediately after key learning moments — reinforce what went well</li>
              <li>💬 Hold weekly 1:1s to answer questions and redirect any misunderstandings</li>
              <li>🌟 When ready, assign “solo but supervised” tasks to assess readiness</li>
            </ul>
            <p><span class="highlight-blue">Training should be personal, actionable, and empowering — not a box to check.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Every great team member starts with great guidance.</span> 
              Build pathways that elevate, not overwhelm. Let them observe mastery, then practice with purpose.
            </p>
          </section>
        `
      },
      performanceReviews: {
        title: "Performance Reviews & Feedback Cycles",
        body: `
          <section>
            <h3>🪞 Clarity, Growth, and Recognition</h3>
            <p>
              At Pawsible, we treat feedback as fuel. Our <span class="highlight-green">performance reviews are designed to guide growth, align expectations, and celebrate progress</span>. 
              Feedback should feel <span class="highlight">frequent, fair, and focused</span> — not feared.
            </p>
          </section>
      
          <section>
            <h4>📅 1. Review Schedule & Cadence</h4>
            <ul>
              <li>🆕 <strong>30-Day Check-In:</strong> After onboarding, review soft skills, SOP grasp, and culture fit</li>
              <li>📈 <strong>Quarterly Reviews:</strong> Focus on performance, reliability, teamwork, and role-specific goals</li>
              <li>🌟 <strong>Ad Hoc Feedback:</strong> Real-time praise or course correction when needed (documented)</li>
            </ul>
            <p><span class="highlight-blue">Frequent feedback prevents surprise — it builds momentum and trust.</span></p>
          </section>
      
          <section>
            <h4>🔍 2. What We Evaluate</h4>
            <ul>
              <li>📋 SOP adherence and task consistency</li>
              <li>🤝 Teamwork and communication with staff and clients</li>
              <li>🧠 Problem-solving and response to constructive feedback</li>
              <li>🌱 Initiative, positivity, and contribution to company culture</li>
              <li>💡 Role-specific metrics (e.g. groom time, shuttle incident logs, training feedback)</li>
            </ul>
            <p><span class="highlight-green">We evaluate to elevate — the goal is improvement, not perfection.</span></p>
          </section>
      
          <section>
            <h4>🗣️ 3. Feedback Delivery Best Practices</h4>
            <ul>
              <li>🪞 Be honest, direct, and kind — highlight strengths before addressing growth areas</li>
              <li>📊 Use examples and data, not vague feelings</li>
              <li>📣 Allow team member response, reflection, and collaboration on improvement plans</li>
              <li>📝 Log feedback and action steps in the staff dashboard</li>
            </ul>
            <p><span class="highlight-blue">Constructive feedback should feel like support, not punishment.</span></p>
          </section>
      
          <section>
            <h4>🌟 4. Recognition & Motivation</h4>
            <ul>
              <li>🎉 Shout-outs in meetings or team chats for great work</li>
              <li>🏅 Monthly "Pawsible Pro" spotlight for high-performing or most-improved team members</li>
              <li>💬 Ask: “What kind of recognition feels meaningful to you?”</li>
            </ul>
            <p><span class="highlight-green">Celebrating wins builds buy-in — especially when it’s personalized.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Performance reviews aren’t just about evaluation — they’re about evolution.</span> 
              At Pawsible, we invest in our people because their growth is our growth.
            </p>
          </section>
        `
      },
      conflictResolution: {
        title: "Conflict Resolution & Staff Mediation",
        body: `
          <section>
            <h3>🤝 From Friction to Forward Movement</h3>
            <p>
              At Pawsible, we know tension can arise in high-energy environments. <span class="highlight">What matters most is how we navigate it</span>. 
              This SOP ensures <span class="highlight-green">conflicts are addressed promptly, fairly, and professionally</span> — before they impact team morale or pet safety.
            </p>
          </section>
      
          <section>
            <h4>🧭 1. Core Principles of Mediation</h4>
            <ul>
              <li>🛑 Address issues early — don’t let resentment build</li>
              <li>👂 Listen to understand, not just respond</li>
              <li>💬 Keep the focus on behaviors, not personalities</li>
              <li>🌱 Assume positive intent, but hold boundaries</li>
              <li>🧠 Stay neutral — avoid taking sides unless there's clear misconduct</li>
            </ul>
            <p><span class="highlight-blue">Resolution isn’t about winning — it’s about restoring trust and alignment.</span></p>
          </section>
      
          <section>
            <h4>📋 2. Step-by-Step Conflict Handling</h4>
            <ul>
              <li>👂 <strong>Step 1:</strong> Allow each party to share their perspective — uninterrupted</li>
              <li>🔍 <strong>Step 2:</strong> Clarify what happened and what was felt — seek facts and emotions</li>
              <li>🎯 <strong>Step 3:</strong> Identify shared goals (team success, safe dogs, client happiness)</li>
              <li>🛠️ <strong>Step 4:</strong> Brainstorm solutions or action steps — together</li>
              <li>📝 <strong>Step 5:</strong> Document the resolution privately and follow up in 1 week</li>
            </ul>
            <p><span class="highlight-green">Most conflicts can be softened with clarity, empathy, and good listening.</span></p>
          </section>
      
          <section>
            <h4>🚩 3. Escalation Criteria</h4>
            <ul>
              <li>🚫 Repeated behavior after coaching</li>
              <li>⚠️ Safety concerns involving pets, clients, or staff</li>
              <li>💥 Highly emotional or disruptive incidents</li>
              <li>📣 Reports of bullying, harassment, or policy violations</li>
            </ul>
            <p><span class="highlight-red">When escalation is necessary, notify the General Manager and log a formal incident report.</span></p>
          </section>
      
          <section>
            <h4>🌟 4. Preventative Culture Practices</h4>
            <ul>
              <li>🧘 Weekly check-ins or huddles to surface small issues early</li>
              <li>💬 Open-door policy for feedback and coaching</li>
              <li>🎉 Celebrate team wins together — gratitude reduces friction</li>
              <li>🛡️ Encourage emotional safety: “Speak up” culture with respect</li>
            </ul>
            <p><span class="highlight-blue">Healthy teams address tension before it festers. Prevention is the best intervention.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Conflict is inevitable — disrespect is not.</span>  
              As leaders, we model how to pause, reset, and repair. At Pawsible, every team member deserves to feel heard, respected, and supported.
            </p>
          </section>
        `
      },
      interdepartmentalFlow: {
        title: "Interdepartmental Communication Flow",
        body: `
          <section>
            <h3>🔗 One Team, One Pawsible</h3>
            <p>
              Seamless communication across departments is the backbone of our care model. Whether it's a grooming note that impacts daycare or a shuttle delay that affects front desk check-in, <span class="highlight">information must flow cleanly across roles</span> to ensure top-tier service and safety.
            </p>
          </section>
      
          <section>
            <h4>🧭 1. Core Communication Principles</h4>
            <ul>
              <li>📲 Always use shared tools (dashboard, Slack) — avoid verbal-only handoffs</li>
              <li>🧠 Think “who else needs to know?” — communicate with foresight</li>
              <li>⏱️ Share updates in real time — don’t wait until the end of your shift</li>
              <li>🤝 Respect each department’s responsibilities and timelines</li>
            </ul>
            <p><span class="highlight-blue">Clear updates prevent confusion — and make life easier for everyone down the line.</span></p>
          </section>
      
          <section>
            <h4>📋 2. Examples of Required Cross-Team Updates</h4>
            <ul>
              <li>🛁 Grooming notes (ear infection, matting, skin issues) → alert daycare & front desk</li>
              <li>🚐 Shuttle delay or no-show → notify front desk and daycare teams immediately</li>
              <li>🐾 Training session progress → share with daycare for follow-through on cues</li>
              <li>🧺 Boarding-specific food/med issues → loop in grooming, daycare, and front desk</li>
              <li>💬 Incident logs or behavior concerns → notify all teams who interact with the pet</li>
            </ul>
            <p><span class="highlight-green">Cross-team care builds confidence — for clients, pets, and coworkers alike.</span></p>
          </section>
      
          <section>
            <h4>📲 3. Channels We Use (and When)</h4>
            <ul>
              <li>📆 Pawsible Dashboard: Client info, pet profiles, service updates, shuttle routing</li>
              <li>💬 Slack: Real-time handoffs, alerts, internal discussion</li>
              <li>📝 Incident Log: Document issues for manager review and cross-departmental visibility</li>
              <li>📞 Phone/Text (only if urgent): Use professionally, follow up in app afterward</li>
            </ul>
            <p><span class="highlight-yellow">Use the right channel for the message — urgent items need speed, long-term items need documentation.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Communication is a service skill — and a safety tool.</span>  
              Every team member, every shift, every update matters. When departments move in sync, Pawsible operates at its best.
            </p>
          </section>
        `
      },
      dailyTaskOversight: {
        title: "Daily Task Board Review & Shift Oversight",
        body: `
          <section>
            <h3>📋 Eyes on the Flow, Hands on the Pulse</h3>
            <p>
              The Daily Task Board is your control panel. As a manager, your role is to <span class="highlight-green">monitor, support, and optimize team performance in real time</span>. 
              From grooming time blocks to daycare medication reminders, <span class="highlight">oversight ensures nothing falls through the cracks</span>.
            </p>
          </section>
      
          <section>
            <h4>🔁 1. Daily Task Board Review Protocol</h4>
            <ul>
              <li>📲 Check all department boards at the start, midpoint, and end of shift</li>
              <li>✅ Ensure time-sensitive items (medications, shuttle alerts, feeding) are marked completed</li>
              <li>🧠 Scan for red flags: unfinished tasks, unclear notes, missed check-ins</li>
              <li>📌 Assign or reassign tasks proactively if staff fall behind or need help</li>
            </ul>
            <p><span class="highlight-blue">Visibility is leadership — catch gaps before they become client complaints.</span></p>
          </section>
      
          <section>
            <h4>🧭 2. Shift Oversight Expectations</h4>
            <ul>
              <li>🗂️ Confirm roles and task distribution at beginning of shift (groomers, floaters, desk, etc.)</li>
              <li>📍 Walk the floor 2–3x per shift — grooming, daycare, boarding, lobby</li>
              <li>👂 Be available for quick check-ins, shadowing, or redirecting unclear SOP execution</li>
              <li>🧾 Check for clock-in/out accuracy and team breaks as appropriate</li>
            </ul>
            <p><span class="highlight-green">Your presence is assurance — when staff feel supported, they perform stronger and with more pride.</span></p>
          </section>
      
          <section>
            <h4>📊 3. End-of-Shift Accountability Wrap</h4>
            <ul>
              <li>📲 Review final task board — all tasks should be marked done or commented on if delayed</li>
              <li>📝 Leave manager notes on dashboard if something needs carry-over or follow-up</li>
              <li>📸 Optional: Take photo or digital backup of completed board if needed for records</li>
              <li>🎯 Briefly debrief with next shift lead if handoff is required</li>
            </ul>
            <p><span class="highlight-yellow">Documentation prevents miscommunication — close every shift with clarity.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Great managers lead from the ground, not from a clipboard.</span>  
              Oversight isn’t about hovering — it’s about guiding, supporting, and ensuring every tail is wagging and every human feels seen.
            </p>
          </section>
        `
      },
      clientRecovery: {
        title: "Client Complaint Response & Recovery",
        body: `
          <section>
            <h3>🛠️ Turning Frustration into Trust</h3>
            <p>
              No matter how polished our service, complaints will happen. A strong recovery approach allows us to <span class="highlight-green">turn challenges into loyalty-building moments</span>. 
              At Pawsible, <span class="highlight">we respond with empathy, accountability, and solutions — not excuses</span>.
            </p>
          </section>
      
          <section>
            <h4>📞 1. Immediate Acknowledgment</h4>
            <ul>
              <li>👂 Listen fully without interrupting or correcting the client mid-concern</li>
              <li>🧠 Mirror back the key issue (“I hear that the shuttle arrived late, and it made your morning stressful — that’s completely valid.”)</li>
              <li>📩 Reassure them their concern is being taken seriously and will be addressed quickly</li>
              <li>📝 Log complaint details in the internal communication log or app dashboard</li>
            </ul>
            <p><span class="highlight-blue">Validation calms emotions — it’s the first step in earning back trust.</span></p>
          </section>
      
          <section>
            <h4>🔧 2. Investigate Promptly</h4>
            <ul>
              <li>🔍 Review team task logs, staff notes, photos, or camera footage if relevant</li>
              <li>🗣️ Check in privately with involved team members to understand all sides</li>
              <li>📌 Identify what happened, where the process failed, and what could’ve prevented it</li>
              <li>📲 Prepare a resolution that fits Pawsible standards and the client’s concerns</li>
            </ul>
            <p><span class="highlight-green">Transparency doesn’t mean oversharing — it means being honest, thorough, and fair.</span></p>
          </section>
      
          <section>
            <h4>🎯 3. Communicate Resolution Thoughtfully</h4>
            <ul>
              <li>✉️ Respond by phone or email depending on complaint severity and client preference</li>
              <li>✅ Share clear findings, own the issue where appropriate, and explain next steps taken</li>
              <li>🎁 Offer goodwill gesture if applicable: credit, comped service, or small thank-you gift</li>
              <li>📲 Log resolution summary in the client profile and flag for future service notes</li>
            </ul>
            <p><span class="highlight-yellow">Clients don’t expect perfection — they expect professionalism and care.</span></p>
          </section>
      
          <section>
            <h4>📈 4. Follow-Up After Recovery</h4>
            <ul>
              <li>🗓️ Set a calendar reminder to check in with the client after their next visit</li>
              <li>😊 Thank them again for giving feedback and trusting us with their pet</li>
              <li>🔄 Internally review if SOP updates or training refreshers are needed</li>
            </ul>
            <p><span class="highlight-blue">Recovery is complete when the client feels seen, not just refunded.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Every complaint is a second chance.</span>  
              Handle it with warmth, speed, and sincerity — and clients will remember how we made them feel far more than what went wrong.
            </p>
          </section>
        `
      },
      incidentFollowUp: {
        title: "Incident Follow-Up & Documentation",
        body: `
          <section>
            <h3>📋 Accountability After the Incident</h3>
            <p>
              At Pawsible, <span class="highlight">how we respond after an incident matters just as much as what happened</span>. 
              Proper documentation and follow-through ensure <span class="highlight-green">transparency, learning, and safety improvements</span> — for pets, staff, and parents alike.
            </p>
          </section>
      
          <section>
            <h4>📝 1. Immediate Post-Incident Actions</h4>
            <ul>
              <li>⏱️ Within 1 hour of the incident, ensure a digital incident report is submitted with:
                <ul>
                  <li>📍 Time, location, and team members present</li>
                  <li>🐶 Pets involved and nature of the incident</li>
                  <li>👣 Sequence of events — brief and factual</li>
                  <li>🚨 Any physical harm, staff response, and actions taken</li>
                </ul>
              </li>
              <li>📸 Upload photos if needed (e.g., minor injury, damaged gear)</li>
              <li>📲 Notify manager and flag for review within internal task system</li>
            </ul>
            <p><span class="highlight-blue">Clarity and accuracy protect everyone — write like it could be reviewed by legal or a parent.</span></p>
          </section>
      
          <section>
            <h4>📞 2. Parent Notification & Tone</h4>
            <ul>
              <li>📩 For minor incidents: notify via message or email with factual recap and reassurance</li>
              <li>📞 For moderate to major incidents: call parent directly, keep calm and professional</li>
              <li>✅ Share how the situation was handled and what steps are in place to avoid recurrence</li>
              <li>🔒 Never speculate or assign blame — focus on facts, care provided, and follow-up plans</li>
            </ul>
            <p><span class="highlight-green">Trust is built when we’re proactive, honest, and solution-oriented — even in tough moments.</span></p>
          </section>
      
          <section>
            <h4>🔁 3. Internal Review & Learning</h4>
            <ul>
              <li>📆 Manager reviews all incidents weekly for repeat patterns or SOP gaps</li>
              <li>💬 If warranted, debrief incident in staff huddle or department channel</li>
              <li>📚 Use as coaching moment if behavior correction, supervision error, or handling missteps contributed</li>
              <li>🛠️ Adjust training, scheduling, or processes if a trend is found</li>
            </ul>
            <p><span class="highlight-yellow">Incidents are data. Use them to grow — not to punish.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Incidents are inevitable — but disorganization is optional.</span>  
              Every well-handled follow-up strengthens our reputation as a safe, professional, and accountable pet care team.
            </p>
          </section>
        `
      },
      scheduleAdjustments: {
        title: "Schedule Adjustments & Coverage",
        body: `
          <section>
            <h3>📆 Smooth Transitions, No Gaps</h3>
            <p>
              At Pawsible, we understand that life happens — but <span class="highlight">schedule changes must be handled with structure, clarity, and accountability</span>. 
              This SOP ensures <span class="highlight-green">shift coverage stays seamless, fair, and focused on pet safety and team cohesion</span>.
            </p>
          </section>
      
          <section>
            <h4>📋 1. Staff-Initiated Time-Off or Shift Changes</h4>
            <ul>
              <li>🗓️ All requests must go through the official scheduling tool or shift request form — no DMs or verbal swaps</li>
              <li>📅 Give at least 7 days notice for non-emergency time off</li>
              <li>🔄 If requesting to swap a shift, <span class="highlight-green">both parties must confirm via scheduler platform</span></li>
              <li>📲 Notify a manager once the change is logged for final approval</li>
            </ul>
            <p><span class="highlight-blue">Transparency prevents gaps and frustration — use the process every time.</span></p>
          </section>
      
          <section>
            <h4>🧩 2. Manager-Initiated Adjustments (Emergencies or Coverage Needs)</h4>
            <ul>
              <li>🚨 In case of call-outs or emergencies, manager will:
                <ul>
                  <li>📲 First check shift board for available cross-trained staff</li>
                  <li>📞 Text or call suitable team members directly if needed</li>
                  <li>📋 Log changes in shift tracker and notify all affected departments</li>
                </ul>
              </li>
              <li>✅ Any changes will be posted on internal board or shared via shift channel for visibility</li>
            </ul>
            <p><span class="highlight-yellow">Coverage isn’t just about filling space — it’s about placing the right person with the right skill set.</span></p>
          </section>
      
          <section>
            <h4>🤝 3. Fairness & Coverage Culture</h4>
            <ul>
              <li>📊 Track swaps and favors — avoid the same staff being leaned on repeatedly</li>
              <li>🧠 Managers should consider shift fatigue and role match when assigning subs</li>
              <li>🗣️ Encourage proactive communication when staff foresee schedule conflicts</li>
              <li>📈 Reward reliability — track attendance and punctuality as part of performance reviews</li>
            </ul>
            <p><span class="highlight-blue">Healthy shift culture supports trust, teamwork, and operational consistency.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Schedules shift — but standards shouldn’t.</span>  
              At Pawsible, <strong>how we handle coverage reflects our care for each other and the animals we serve</strong>.
            </p>
          </section>
        `
      },
      suppliesVendorCoordination: {
        title: "Supplies Ordering & Vendor Coordination",
        body: `
          <section>
            <h3>📦 Keep It Stocked, Keep It Smooth</h3>
            <p>
              From pee pads to grooming blades, every item we use impacts daily flow. <span class="highlight">Pawsible managers are responsible for proactive supply management</span> and <span class="highlight-green">building strong vendor relationships to keep the team fully equipped</span>.
            </p>
          </section>
      
          <section>
            <h4>🧾 1. Weekly Inventory Review</h4>
            <ul>
              <li>📋 Check inventory every Thursday using department-specific supply checklists</li>
              <li>🔍 Focus on fast-moving essentials: cleaning supplies, food, treats, grooming tools, paper goods</li>
              <li>✅ Confirm stock levels in shared dashboard and flag anything under minimum threshold</li>
              <li>📦 Include client-facing items like retail treats or enrichment toys for reorder if low</li>
            </ul>
            <p><span class="highlight-blue">Routine reviews prevent last-minute scrambles and maintain operational confidence.</span></p>
          </section>
      
          <section>
            <h4>📦 2. Placing Supply Orders</h4>
            <ul>
              <li>🛍️ Submit orders by Friday noon to ensure delivery before the next week</li>
              <li>📠 Use Pawsible's vendor ordering template and submit through designated portal or vendor contact</li>
              <li>💬 Confirm delivery ETA with vendor and log it on shared delivery calendar</li>
              <li>📥 Notify department leads when high-need items are back in stock</li>
            </ul>
            <p><span class="highlight-green">Timely orders keep every team running smoothly — delay affects multiple departments.</span></p>
          </section>
      
          <section>
            <h4>📞 3. Vendor Relationships & Communication</h4>
            <ul>
              <li>🤝 Maintain consistent point-of-contact relationships with vendors (email + phone on file)</li>
              <li>📸 Document damaged shipments or order errors and notify vendor within 24 hours</li>
              <li>📁 Keep vendor terms, pricing sheets, and order history in shared folder for transparency</li>
              <li>📊 Reassess vendor performance biannually — price, speed, reliability, and service</li>
            </ul>
            <p><span class="highlight-blue">Vendors are partners — treat them with professionalism and clear communication.</span></p>
          </section>
      
          <section>
            <h4>🧼 4. Supplies Usage & Waste Prevention</h4>
            <ul>
              <li>📍 Ensure all staff use products per instructions to avoid waste (e.g., concentrated cleaners)</li>
              <li>📦 Store backup items in labeled bins and rotate older stock to front</li>
              <li>🧾 Track high-use items (treats, laundry detergent, wipes) for bulk pricing opportunities</li>
            </ul>
            <p><span class="highlight-yellow">Saving supplies = saving budget. Every item counts toward professional, efficient care.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Supplies are the silent support behind every Pawsible service.</span>  
              Managers keep the engine running by staying ahead, staying organized, and staying connected to our vendors and teams.
            </p>
          </section>
        `
      },
      managerFacilities: {
        title: "Facilities Escalation & Maintenance Requests",
        body: `
          <section>
            <h3>🛠️ Keeping the Space Safe, Functional & Professional</h3>
            <p>
              From a jammed crate door to HVAC issues, facilities management is a core responsibility. 
              <span class="highlight">Managers are the first line of defense in recognizing, logging, and resolving facility concerns</span> — quickly and professionally.
            </p>
          </section>
      
          <section>
            <h4>🔍 1. Identifying Issues Proactively</h4>
            <ul>
              <li>🧼 Do a full walk-through of all service areas at least once per shift (open/close)</li>
              <li>💡 Check lighting, ventilation, water flow, doors/gates, and flooring for wear or hazard</li>
              <li>🪵 Check for minor damage or deterioration in walls, trim, fences, fixtures, or play equipment</li>
              <li>📋 Invite staff to report issues via the internal maintenance log or task board</li>
            </ul>
            <p><span class="highlight-blue">Proactive attention = fewer emergencies and better staff morale.</span></p>
          </section>
      
          <section>
            <h4>📲 2. Reporting and Logging Maintenance Requests</h4>
            <ul>
              <li>📝 Log all issues (even minor) in the shared “Facilities Tracker” with date, location, and description</li>
              <li>📸 Include clear photos if relevant (e.g. leaking sink, broken latch)</li>
              <li>📞 For urgent safety or hygiene issues, contact vendor or handyman immediately and note ETA</li>
              <li>📌 Assign tasks internally (if appropriate) and mark completed when resolved</li>
            </ul>
            <p><span class="highlight-green">Clear logs create accountability — and a clear chain of action.</span></p>
          </section>
      
          <section>
            <h4>🚨 3. Urgent Escalations</h4>
            <ul>
              <li>🚫 Crate/gate safety issues, flooding, electrical malfunctions, or HVAC failure must be escalated immediately</li>
              <li>📞 Call designated emergency vendor or maintenance contact (info stored in vendor list)</li>
              <li>📲 Alert leadership group chat or escalation channel with real-time updates</li>
              <li>📁 Document what was done and when — time of report, vendor ETA, resolution photo</li>
            </ul>
            <p><span class="highlight-red">Don’t delay escalation — safety issues spread fast if ignored.</span></p>
          </section>
      
          <section>
            <h4>🔄 4. Follow-Up & Preventive Maintenance</h4>
            <ul>
              <li>📆 Schedule routine checks for HVAC, water heaters, washer/dryers, and drains monthly</li>
              <li>🔁 Rotate inspection zones each week for full-coverage maintenance</li>
              <li>🧰 Keep spare parts stocked for common fixes (e.g., gate latches, mop heads, crate wheels)</li>
              <li>📊 Review facility notes during monthly manager meetings and update priority projects</li>
            </ul>
            <p><span class="highlight-yellow">Prevention beats reaction — a safe space is a brand promise.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">The quality of care begins with the quality of space.</span>  
              A well-maintained facility protects pets, empowers staff, and reflects Pawsible’s high standards.
            </p>
          </section>
        `
      },
      managerClosingChecklist: {
        title: "End-of-Day Walkthrough & Lock-Up",
        body: `
          <section>
            <h3>🔒 Secure, Sanitize, and Set Up for Tomorrow</h3>
            <p>
              Every great day at Pawsible ends with an <span class="highlight-green">intentional and consistent closing walkthrough</span>. 
              It’s the manager’s job to ensure the facility is <span class="highlight">clean, locked, and fully reset</span> — not just closed.
            </p>
          </section>
      
          <section>
            <h4>🧼 1. Facility Cleanliness Review</h4>
            <ul>
              <li>🧹 Floors swept, mopped, and hair-free in all rooms (grooming, daycare, training, shuttle)</li>
              <li>🧴 All surfaces wiped down (reception desk, grooming tables, crate tops, etc.)</li>
              <li>🚮 Trash and dirty laundry taken out — no odors or overflow</li>
              <li>🐾 Clean pee pads replaced, crates sanitized, bowls stacked neatly</li>
              <li>🧽 Bathroom checked, stocked, and wiped (public + staff)</li>
            </ul>
            <p><span class="highlight-blue">Clean tonight = calm tomorrow. Don’t leave problems for the opener.</span></p>
          </section>
      
          <section>
            <h4>📋 2. End-of-Day Task Review</h4>
            <ul>
              <li>📲 Check each department's task board for completion (boarding, grooming, daycare, shuttle)</li>
              <li>✅ Ensure all feeding logs, medication updates, training notes, and report cards are submitted</li>
              <li>📌 Flag any unresolved tasks for morning shift in team chat</li>
              <li>📸 Optional: Snap photo of completed task boards for reference</li>
            </ul>
            <p><span class="highlight-green">Accountability is built daily — and clear task closure prevents miscommunication.</span></p>
          </section>
      
          <section>
            <h4>🔐 3. Facility Lock-Up Procedure (If closing)</h4>
            <ul>
              <li>🚪 Lock all external doors (front, side, grooming exit, shuttle entry)</li>
              <li>💡 Turn off all lights except safety/nightlights as assigned</li>
              <li>🔒 Check all crate latches, gates, and door locks</li>
              <li>🎥 Confirm all security cameras are operational and night mode is active</li>
              <li>📲 Set alarm system and confirm receipt if monitored remotely</li>
            </ul>
            <p><span class="highlight-yellow">The final 5 minutes of your shift protect the next 12 hours.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">A thoughtful close is a leadership act.</span>  
              When the last team member walks out with confidence, safety, and pride — the entire Pawsible culture benefits.
            </p>
          </section>
        `
      },
      policyRollout: {
        title: "New Program or Policy Rollout Protocol",
        body: `
          <section>
            <h3>🚀 From Concept to Clarity: Rolling Out Programs & Policies</h3>
            <p>
              Whether it’s a new service, app feature, training structure, or internal guideline, <span class="highlight-green">smooth rollouts are key to Pawsible's adaptability and professionalism</span>. 
              This SOP ensures every update is <span class="highlight">understood, supported, and executed consistently</span> across departments.
            </p>
          </section>
      
          <section>
            <h4>🛠️ 1. Pre-Rollout Preparation</h4>
            <ul>
              <li>🧠 Understand the “why”: What problem is this solving or what value does it bring?</li>
              <li>📋 Draft a clear summary of the program or policy including who it impacts, how it works, and when it takes effect</li>
              <li>🔍 Identify potential friction points across staff roles and client touchpoints</li>
              <li>✅ Confirm leadership has aligned on messaging and launch timeline</li>
            </ul>
            <p><span class="highlight-blue">Clarity up front prevents confusion down the line — start with full internal alignment.</span></p>
          </section>
      
          <section>
            <h4>📣 2. Staff Communication & Training</h4>
            <ul>
              <li>📆 Schedule short rollout briefings (department meetings, huddles, or shift change reviews)</li>
              <li>📄 Provide written documentation or cheat sheets where applicable</li>
              <li>🎓 Offer quick demos, FAQs, or test scenarios for tools or workflows</li>
              <li>💬 Create a designated space in team chat or platform for rollout-related questions</li>
            </ul>
            <p><span class="highlight-green">Every rollout is a chance to build team buy-in — treat communication like a campaign, not a memo.</span></p>
          </section>
      
          <section>
            <h4>🧩 3. Cross-Department Coordination</h4>
            <ul>
              <li>🧾 Ensure front desk understands any new messaging or booking flow</li>
              <li>📲 Confirm app notifications or system flags align with rollout goals</li>
              <li>🗓️ Adjust calendar templates or task boards if rollout affects daily operations</li>
              <li>🎯 Create short-term checkpoints or goals to evaluate adoption and results</li>
            </ul>
            <p><span class="highlight-yellow">A smooth launch feels seamless to clients — that’s the result of great internal communication.</span></p>
          </section>
      
          <section>
            <h4>📊 4. Monitoring & Adjustments</h4>
            <ul>
              <li>📝 Check for staff feedback within 1 week of rollout — survey or informal</li>
              <li>🛠️ Adjust procedures or messaging based on early learnings</li>
              <li>📈 Report rollout progress or client response to leadership</li>
              <li>🔁 Schedule 30-day post-launch check-in to finalize, scale, or retire any parts of the policy</li>
            </ul>
            <p><span class="highlight-blue">Policies evolve — tracking what works and what confuses is what makes them stick.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">A policy is only as strong as its rollout.</span> 
              With intentional planning, team training, and responsive tweaks, Pawsible evolves with strength — not stress.
            </p>
          </section>
        `
      },
      staffPromotionCriteria: {
        title: "Trainee to Lead Promotion Criteria",
        body: `
          <section>
            <h3>🌟 Growing the Pack: From Trainee to Lead</h3>
            <p>
              At Pawsible, promotions aren’t based on time alone — they’re earned through <span class="highlight-green">skill mastery, emotional intelligence, leadership behavior, and reliability</span>. 
              This SOP outlines the <span class="highlight">clear, actionable path from new hire to team lead</span> across departments.
            </p>
          </section>
      
          <section>
            <h4>🎓 1. Trainee Milestones</h4>
            <ul>
              <li>📘 Completion of onboarding modules and training pathways</li>
              <li>📝 Passing all required assessments or hands-on reviews (grooming demo, training eval, group play supervision, etc.)</li>
              <li>⏱️ Consistent on-time attendance and shift responsibility</li>
              <li>🧠 Demonstrated understanding of safety, SOPs, and service expectations</li>
            </ul>
            <p><span class="highlight-blue">Master the basics with consistency and curiosity — that’s what graduates you from “in training.”</span></p>
          </section>
      
          <section>
            <h4>🚀 2. Promotion Readiness Indicators</h4>
            <ul>
              <li>🤝 Strong team collaboration, initiative, and maturity under pressure</li>
              <li>📣 Communicates clearly, respectfully, and proactively with peers and managers</li>
              <li>🎯 Independently upholds SOPs, safety, and client standards without needing reminders</li>
              <li>🧩 Seeks out additional responsibilities (training new hires, organizing, leading enrichment blocks)</li>
              <li>📲 Accurately logs tasks, app updates, and incident reports</li>
            </ul>
            <p><span class="highlight-green">You don’t wait to be asked — you notice, you act, and you follow through.</span></p>
          </section>
      
          <section>
            <h4>🔍 3. Review & Approval Process</h4>
            <ul>
              <li>📆 Eligible staff may request review after 60–90 days or be nominated by management</li>
              <li>🧠 Supervisor will review skill evaluations, task logs, and peer feedback</li>
              <li>💬 1-on-1 check-in with leadership to discuss readiness, career goals, and expectations of the lead role</li>
              <li>✅ If approved, new title and pay tier is issued with added responsibilities</li>
            </ul>
            <p><span class="highlight-yellow">Promotions reflect both what you’ve done and how you’ve shown up for the team.</span></p>
          </section>
      
          <section>
            <h4>🏆 4. Lead-Level Expectations</h4>
            <ul>
              <li>🗂️ Supports onboarding and shadowing of new hires</li>
              <li>🧭 Acts as point of contact during shift if supervisor is unavailable</li>
              <li>🛠️ Helps enforce SOPs and models positive team culture</li>
              <li>📝 Flags concerns early and communicates calmly in challenging moments</li>
            </ul>
            <p><span class="highlight-blue">Being a Lead means being the calm in the chaos — the one others can look to and lean on.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Growth at Pawsible is earned — and supported.</span> 
              If you’re coachable, dependable, and committed to care, we’ll give you every chance to lead with pride.
            </p>
          </section>
        `
      },
      managerEmergencyResponse: {
        title: "Emergency Leadership Response Protocol",
        body: `
          <section>
            <h3>🚨 Leadership in Crisis: Emergency Response at Pawsible</h3>
            <p>
              As a manager, your role during emergencies is to <span class="highlight-green">respond swiftly, stabilize the situation, and lead with clarity</span>. 
              This protocol outlines how to guide your team through <span class="highlight">high-stress incidents with professionalism, empathy, and efficiency</span>.
            </p>
          </section>
      
          <section>
            <h4>⚠️ 1. Types of Emergencies Covered</h4>
            <ul>
              <li>🐶 Pet medical emergency (seizure, collapse, choking, etc.)</li>
              <li>🚒 Facility threat (fire, gas leak, break-in, power outage)</li>
              <li>🛑 Human incident (injury, client altercation, staff distress)</li>
              <li>🌪️ Natural disaster or severe weather impact</li>
            </ul>
            <p><span class="highlight-red">The manager is the central command — own the calm, delegate fast, and take decisive action.</span></p>
          </section>
      
          <section>
            <h4>📋 2. Immediate Action Checklist</h4>
            <ul>
              <li>🧠 Assess and confirm the nature/severity of the emergency</li>
              <li>📲 Alert the appropriate responders:
                <ul>
                  <li>Pet injury: contact emergency vet + notify parent</li>
                  <li>Human injury: apply first aids, call 911 if needed, and file internal report</li>
                  <li>Facility threat: evacuate if necessary, call relevant authorities</li>
                </ul>
              </li>
              <li>📢 Direct staff calmly: assign roles (e.g., one person comforts animal, one gets medical supplies)</li>
              <li>📑 Begin logging details (time, who responded, actions taken)</li>
            </ul>
            <p><span class="highlight-blue">Stabilize first, document second — both are essential for effective leadership.</span></p>
          </section>
      
          <section>
            <h4>📞 3. Communication Protocols</h4>
            <ul>
              <li>📩 Notify the client as soon as the pet is safe and medical actions are in progress</li>
              <li>📲 Notify ownership or senior leadership with a concise status update</li>
              <li>📝 Log the incident in the dashboard with key timestamps and decisions made</li>
            </ul>
            <p><span class="highlight-green">Keep everyone informed — silence during emergencies creates confusion and erodes trust.</span></p>
          </section>
      
          <section>
            <h4>🔁 4. Post-Emergency Leadership Duties</h4>
            <ul>
              <li>📋 Complete full incident report with all involved staff listed</li>
              <li>🧼 Debrief team — acknowledge efforts, review what went well and what to improve</li>
              <li>🛠️ Coordinate with owner for follow-up (client call, refund, training refreshers, safety upgrades)</li>
              <li>👂 Check in with staff emotionally if incident was traumatic</li>
            </ul>
            <p><span class="highlight-yellow">What happens *after* the emergency defines your leadership. Debrief. Document. Grow.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Emergencies reveal who we are under pressure.</span>  
              Your clarity, presence, and compassion will ripple through your team and client trust long after the moment has passed.
            </p>
          </section>
        `
      }                  
}